import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";

import "./Login.css";
import Footer from "../components/Footer";
import { getFirebaseAuth, isFirebaseConfigured } from "../utils/firebaseConfig";

import bgVideo from "../assets/bgVideo.mp4";
import leftLogo from "../assets/ciallogo.png";
import rightLogo from "../assets/krrlogo.jpg";

const API_URL = process.env.REACT_APP_API_URL;

// Steps: username -> otp -> newPassword -> done
function ResetPasswordFirebase() {

    const navigate = useNavigate();

    const [step, setStep] = useState("username");

    const [username, setUsername] = useState("");

    const [maskedPhone, setMaskedPhone] = useState("");

    const [otp, setOtp] = useState("");

    const [newPassword, setNewPassword] = useState("");

    const [confirmPassword, setConfirmPassword] = useState("");

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Holds the Firebase confirmationResult between the "send OTP"
    // and "verify OTP" steps - and the ID token between "verify OTP"
    // and the final password submission.
    const [confirmationResult, setConfirmationResult] = useState(null);

    const [firebaseIdToken, setFirebaseIdToken] = useState(null);

    const maskPhone = (phone) => {

        const digits = phone.replace(/\D/g, "");

        if (digits.length < 4) return phone;

        return `xxxxxx${digits.slice(-4)}`;

    };

    const handleLookupPhone = async (e) => {

        e.preventDefault();

        if (!isFirebaseConfigured()) {

            toast.error("Password reset via SMS isn't configured on this deployment yet. Contact your administrator.");
            return;

        }

        setIsSubmitting(true);

        try {

            const res = await fetch(`${API_URL}/portal/lookup-phone-for-reset`, {

                method: "POST",

                headers: { "Content-Type": "application/json" },

                body: JSON.stringify({ username })

            });

            const data = await res.json();

            if (!data.status) {

                toast.error(data.message || "No account found.");
                return;

            }

            setMaskedPhone(maskPhone(data.mobile_no));

            // Invisible reCAPTCHA, attached to the hidden container
            // rendered below - Firebase requires this to prevent SMS
            // abuse, but "invisible" means the person never sees a
            // challenge unless Firebase's own risk-scoring decides
            // one is needed.
            const auth = getFirebaseAuth();

            const verifier = new RecaptchaVerifier(auth, "recaptcha-container", { size: "invisible" });

            const result = await signInWithPhoneNumber(auth, data.mobile_no, verifier);

            setConfirmationResult(result);

            setStep("otp");

            toast.success(`A verification code was sent to ${maskPhone(data.mobile_no)}.`);

        } catch (err) {

            console.error("Failed to send OTP:", err);

            toast.error(err.message || "Could not send the verification code. Please try again.");

        } finally {

            setIsSubmitting(false);

        }

    };

    const handleVerifyOtp = async (e) => {

        e.preventDefault();

        if (!otp) {

            toast.warn("Enter the verification code you received.");
            return;

        }

        setIsSubmitting(true);

        try {

            const userCredential = await confirmationResult.confirm(otp);

            const idToken = await userCredential.user.getIdToken();

            setFirebaseIdToken(idToken);

            setStep("newPassword");

        } catch (err) {

            console.error("OTP verification failed:", err);

            toast.error("That code wasn't correct or has expired. Please try again.");

        } finally {

            setIsSubmitting(false);

        }

    };

    const handleSetNewPassword = async (e) => {

        e.preventDefault();

        if (!newPassword || newPassword.length < 6) {

            toast.warn("Password must be at least 6 characters.");
            return;

        }

        if (newPassword !== confirmPassword) {

            toast.warn("Passwords don't match.");
            return;

        }

        setIsSubmitting(true);

        try {

            const res = await fetch(`${API_URL}/portal/reset-password-firebase`, {

                method: "POST",

                headers: { "Content-Type": "application/json" },

                body: JSON.stringify({

                    username,

                    firebase_id_token: firebaseIdToken,

                    new_password: newPassword

                })

            });

            const data = await res.json();

            if (data.status) {

                toast.success(data.message);

                setStep("done");

                setTimeout(() => navigate("/"), 2000);

            } else {

                toast.error(data.message || "Could not reset your password.");

            }

        } catch (err) {

            console.error("Failed to reset password:", err);

            toast.error("Something went wrong. Please try again.");

        } finally {

            setIsSubmitting(false);

        }

    };

    return (

        <div className="login-page">

            <video className="background-video" autoPlay muted loop playsInline>
                <source src={bgVideo} type="video/mp4" />
            </video>

            <div className="video-overlay"></div>

            <div className="top-header">

                <img src={leftLogo} alt="CIAL Logo" className="header-logo left-logo" />

                <img src={rightLogo} alt="KRR Logo" className="header-logo right-logo" />

            </div>

            <div className="login-container">

                <div className="login-card">

                    <h2>Reset Password</h2>

                    {

                        !isFirebaseConfigured() &&

                        <p style={{ color: "#fca5a5", fontSize: "13px", textAlign: "center",display: "none", marginBottom: "18px" }}>
                            Password reset via SMS isn't configured on this deployment yet.
                            Contact your administrator.
                        </p>

                    }

                    {

                        step === "username" &&

                        <form onSubmit={handleLookupPhone}>

                            <p style={{ fontSize: "13px", color: "#6a6a9b", marginBottom: "14px" }}>
                                Enter your username. We'll send a verification code by SMS to the
                                phone number on file for your account.
                            </p>

                            <div className="form-group">
                                <label>Username</label>

                                <input
                                    type="text"
                                    placeholder="Enter your username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    disabled={isSubmitting}
                                    required
                                />
                            </div>

                            <button className="login-btn" disabled={isSubmitting}>
                                {isSubmitting ? "Sending..." : "Send Verification Code"}
                            </button>

                            <Link to="/" className="forgot-password-link">
                                Back to login
                            </Link>

                        </form>

                    }

                    {

                        step === "otp" &&

                        <form onSubmit={handleVerifyOtp}>

                            <p style={{ fontSize: "13px", color: "#e5e7eb", marginBottom: "14px" }}>
                                Enter the code sent to {maskedPhone}.
                            </p>

                            <div className="form-group">
                                <label>Verification Code</label>

                                <input
                                    type="text"
                                    placeholder="Enter the code"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    disabled={isSubmitting}
                                    required
                                />
                            </div>

                            <button className="login-btn" disabled={isSubmitting}>
                                {isSubmitting ? "Verifying..." : "Verify Code"}
                            </button>

                        </form>

                    }

                    {

                        step === "newPassword" &&

                        <form onSubmit={handleSetNewPassword}>

                            <p style={{ fontSize: "13px", color: "#e5e7eb", marginBottom: "14px" }}>
                                Code verified. Choose your new password.
                            </p>

                            <div className="form-group">
                                <label>New Password</label>

                                <input
                                    type="password"
                                    placeholder="Enter new password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    disabled={isSubmitting}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Confirm New Password</label>

                                <input
                                    type="password"
                                    placeholder="Confirm new password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    disabled={isSubmitting}
                                    required
                                />
                            </div>

                            <button className="login-btn" disabled={isSubmitting}>
                                {isSubmitting ? "Saving..." : "Reset Password"}
                            </button>

                        </form>

                    }

                    {

                        step === "done" &&

                        <>
                            <p style={{ color: "#86efac", textAlign: "center", marginBottom: "18px" }}>
                                Password reset successfully. Redirecting to login...
                            </p>

                            <Link to="/" className="forgot-password-link">
                                Back to login
                            </Link>
                        </>

                    }

                    {/* Firebase's invisible reCAPTCHA attaches itself here. */}
                    <div id="recaptcha-container"></div>

                </div>

            </div>

            <Footer transparent />

        </div>

    );

}

export default ResetPasswordFirebase;
